<template>
  <div
    ref="form"
    class="p-6"
  >
    <div class="text-xl font-bold text-center mb-3">
      {{ data.title }}
    </div>

    <div
      v-for="(field, index) in formFieldsFormatted"
      :key="field.type + '_' + index"
      class="my-2"
    >
      <component
        v-if="field.type === 'custom'"
        :is="field.component"
        v-model="fields.customFields[field.config.id]"
        :parameters="field"
        :errorStyle="{
          backgroundColor: '#e43a2d',
          color: '#fff7f7'
        }"
      />

      <component
        v-else
        :is="field.component"
        v-model="fields[field.type]"
        :parameters="field"
        :errorStyle="{
          backgroundColor: '#e43a2d',
          color: '#fff7f7'
        }"
      />
    </div>

    <XButton
      class="rounded px-10 font-bold text-white bg-grey-30 mt-3"
      :loading="loading"
      @click="onSubmit"
    >
      {{ $t('event.timeline.buttonActions.save') }}
    </XButton>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'
import _ from 'lodash'

import {
  setCookie
} from '@plat/util/cookie'

import XTextInput from '@plat/views/partials/fields/TextInput'
import XTextareaInput from '@plat/views/partials/fields/TextareaInput'
import XNumberInput from '@plat/views/partials/fields/NumberInput'
import XEmailInput from '@plat/views/partials/fields/EmailInput'
import XBirthYearInput from '@plat/views/partials/fields/BirthYearInput'
import XTitleInput from '@plat/views/partials/fields/TitleInput'
import XGenderInput from '@plat/views/partials/fields/GenderInput'
import XBirthDateInput from '@plat/views/partials/fields/BirthDateInput'
import XSelectInput from '@plat/views/partials/fields/SelectInput'
import XSelectMultipleInput from '@plat/views/partials/fields/SelectMultipleInput'
import XCheckboxInput from '@plat/views/partials/fields/CheckboxInput'
import XDateInput from '@plat/views/partials/fields/DateInput'
import XAgreementInput from '@plat/views/partials/fields/AgreementInput'
import XCountryInput from '@plat/views/partials/fields/CountryInput'
import XEmojiReactionInput from '@plat/views/partials/fields/EmojiReactionInput'
import XInterestsInput from '@plat/views/partials/fields/InterestsInput'

import FormBaseValidator from '@common/mixins/FormBaseValidator'

export default {
  mixins: [FormBaseValidator],

  provide () {
    return {
      '$validator': this.$validator
    }
  },

  components: {
    XTextInput,
    XTextareaInput,
    XNumberInput,
    XEmailInput,
    XBirthYearInput,
    XTitleInput,
    XGenderInput,
    XBirthDateInput,
    XSelectInput,
    XSelectMultipleInput,
    XDateInput,
    XCheckboxInput,
    XAgreementInput,
    XCountryInput,
    XEmojiReactionInput,
    XInterestsInput
  },

  props: {
    data: {
      type: Object
    }
  },

  data () {
    return {
      currentFormFields: [],
      typeTextList: [
        'firstName',
        'lastName',
        'secondLastName',
        'phone',
        'postalCode'
      ],
      fields: {
        customFields: {}
      },
      loading: false,
      errorMessage: null,
      formHeight: 0
    }
  },

  computed: {
    ...mapState({
      eventId: state => state.routeParameters.event.id,
      eventSlug: state => state.routeParameters.event.slug,
      attendee: state => state.attendee,
      formFields: state => state.formFields || [],
      isPublicTimeline: state => state.isPublicTimeline
    }),

    formFieldsFormatted () {
      return this.formFields
        .filter(
          field => field.type === 'custom'
            ? this.data.value.includes(field.config.id)
            : this.data.value.includes(field.type)
        )
        .map(field => ({
          ...field,
          required: true,
          component: this.typeTextList.includes(field.type)
            ? 'XTextInput'
            : field.type !== 'custom'
              ? 'X' + _.upperFirst(_.camelCase(field.type)) + 'Input'
              : 'X' + _.upperFirst(_.camelCase(field.config.type)) + 'Input'
        }))
    },

    fieldsFilled () {
      return !this.isPublicTimeline
        ? this.formFieldsFormatted
          .some(field => {
            if (field.type === 'custom') {
              let fieldFormatted = Number.isInteger(this.attendee.extra[field.config.id])
                ? _.trim(this.attendee.extra[field.config.id].toString())
                : _.trim(this.attendee.extra[field.config.id] || '')

              return _.isEmpty(fieldFormatted)
            }

            let fieldFormatted = Number.isInteger(this.attendee[field.type])
              ? _.trim(this.attendee[field.type].toString())
              : _.trim(this.attendee[field.type] || '')

            return _.isEmpty(fieldFormatted)
          }) === false
        : false
    }
  },

  watch: {
    attendee: function () {
      if (this.fieldsFilled) {
        this.$emit('rescuePaid')
      } else {
        this.fillFields()
      }
    }
  },

  created () {
    this.$nextTick(() => {
      if (
        !this.isPublicTimeline &&
        this.attendee.id
      ) {
        if (this.fieldsFilled) {
          this.$emit('rescuePaid')
        } else {
          this.fillFields()
        }
      }
    })
  },

  mounted () {
    this.$nextTick(() => {
      this.formHeight = this.$refs.form.offsetHeight
    })
  },

  methods: {
    onSubmit () {
      if (this.loading) {
        return
      }

      this.loading = true
      this.errorMessage = null

      this.validate()
        .then(isValid => {
          if (!isValid) {
            return
          }

          let saveAttendee = this.attendee.id
            ? this.updateAttendee : this.createAttendee

          return saveAttendee()
            .then(result => {
              let attendee = result.data.event
                ? result.data.event.createAttendee
                : result.data.attendee.update

              this.$store.dispatch(
                'storeAttendee',
                attendee
              )

              if (this.isPublicTimeline) {
                setCookie(this.eventId, attendee.id, 365)

                location.href = '/' + this.eventSlug + '/timeline/' + attendee.id
              }

              this.$emit('rescuePaid')
            })
            .catch(errors => {
              let graphQLError = errors.graphQLErrors ? errors.graphQLErrors[0] : null

              this.errorMessage = graphQLError && this.getGraphQlValidationError(graphQLError)
                ? this.getGraphQlValidationError(graphQLError)
                : this.$t('common.notifications.messages.requestError')
            })
        })
        .finally(() => {
          this.loading = false
        })
    },

    createAttendee () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation createAttendee($eventId: Uuid!, $input: CreateAttendeeInput!, $source: String) {
              event (eventId: $eventId) {
                createAttendee(input: $input, source: $source) {
                  id
                }
              }
            }
          `,
          variables: {
            eventId: this.eventId,
            input: this.fields,
            source: 'timeline'
          }
        })
    },

    updateAttendee () {
      return this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateAttendee($attendeeId: Uuid!, $input: UpdateAttendeeInput!) {
              attendee(attendeeId: $attendeeId) {
                update(input: $input) {
                  id
                  firstName
                  lastName
                  secondLastName
                  email
                  phone
                  gender
                  birthYear
                  postalCode
                  ticketType
                  birthDate
                  title
                  country
                  interests
                  extra
                  status
                }
              }
            }
          `,
          variables: {
            attendeeId: this.attendee.id,
            input: this.fields
          }
        })
    },

    initFormFields (field) {
      let customField

      if (field.type === 'custom') {
        customField = this.attendee.extra
          ? this.attendee.extra[field.config.id]
          : this.getDefaultFieldValueByType(field.config.type)

        this.$set(this.fields.customFields, field.config.id, customField)

        return
      }

      this.$set(this.fields, field.type, this.attendee[field.type])
    },

    getDefaultFieldValueByType (type) {
      if (type === 'agreement' || type === 'number') {
        return null
      }

      if (type === 'checkbox') {
        return false
      }

      return ''
    },

    fillFields () {
      this.formFieldsFormatted
        .forEach(field => this.initFormFields(field))
    },

    getGraphQlValidationError (graphQLError) {
      if (graphQLError.extensions.category === 'validationNotPassed') {
        let validationKeys = Object.keys(graphQLError.validation)
        let validationMessage = graphQLError.validation[validationKeys[0]][0]

        return graphQLError.message + ': ' + validationMessage
      }

      return null
    }
  }
}
</script>
